import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Validate token on app load - non-blocking for faster perceived performance
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      // Immediately set user from localStorage for fast render
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      setLoading(false);
      
      // Validate token in background (non-blocking)
      axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/me`,
        { headers: { Authorization: `Bearer ${storedToken}` } }
      ).then(response => {
        // Update with fresh data silently
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }).catch(() => {
        // Token invalid - clear auth and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setToken(null);
        // Only redirect if on a protected route
        const path = window.location.pathname;
        if (!['/', '/login', '/register', '/blog', '/forgot-password', '/reset-password'].includes(path) && !path.startsWith('/blog/') && !path.startsWith('/preview/')) {
          window.location.href = '/login?session=expired';
        }
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      { email, password, remember_me: rememberMe }
    );
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    // Reset sidebar scroll position on new login
    sessionStorage.removeItem('sidebarScrollPos');
    
    return userData;
  };

  const register = async (email, password, name, country = 'AU') => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/register`,
      { email, password, name, country }
    );
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(access_token);
    setUser(userData);
    
    // Reset sidebar scroll position on new registration
    sessionStorage.removeItem('sidebarScrollPos');
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear session flags to allow First-Return experience for next login
    sessionStorage.removeItem('onboarding_just_completed');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    updateUser,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};